import styled from 'styled-components';

import {
  BasicParagraph,
  BiggerSubtitle,
  BigSubtitle,
  SmallParagraph,
} from '@genially/design-system';

import { PANEL_SURFACE } from '../common/panelColors';

export const Wrapper = styled.div(({ theme: { color, size } }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: size.unit,
  padding: size.units(2),
  borderRadius: size.unit,
  backgroundColor: PANEL_SURFACE,
}));

/**
 * Only shown while the card is hovered — or focused, so it is reachable by keyboard —
 * because it is a shortcut, not the point of the card.
 */
export const GoToSlideButton = styled.div(({ theme: { size, transition } }) => ({
  position: 'absolute',
  top: size.unit,
  right: size.unit,
  opacity: 0,
  transition: transition.standard('opacity'),

  [`${Wrapper}:hover &`]: {
    opacity: 1,
  },

  '&:focus-within': {
    opacity: 1,
  },
}));

export const QuestionTitle = styled(BasicParagraph)(({ theme: { color, size } }) => ({
  color: color.content.reversed.default(),
  // Room for the go-to-slide button, so a long statement never runs under it.
  paddingRight: size.units(4),
  display: '-webkit-box',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '2',
  overflow: 'hidden',
}));

export const InfoText = styled(SmallParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(0.7),
}));

/**
 * The winning option is the whole answer to a survey, so it is the headline of the card
 * and takes the place of the bar the autocorrective questions get.
 */
export const OptionLabel = styled(BigSubtitle)(({ theme: { color } }) => ({
  color: color.background.complementary(9),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

/**
 * An open answer has nothing to aggregate, so how many people wrote something is the
 * whole reading, and it takes the headline slot the other charts use.
 */
export const OpenAnswerCount = styled(BigSubtitle)(({ theme: { color } }) => ({
  color: color.background.complementary(3),
}));

/** Same role the winning option plays for a survey: the headline of the card. */
export const AverageValue = styled(BiggerSubtitle)(({ theme: { color } }) => ({
  color: color.background.complementary(4),
}));

/** A span, not a paragraph: it lives inside the heading above. */
export const AverageScale = styled.span(({ theme: { color, font, size } }) => ({
  fontSize: size.units(1.75),
  fontWeight: font.weight.semiLight,
  color: color.content.reversed.default(0.7),
}));

export const BarTrack = styled.div(({ theme: { color, size } }) => ({
  width: '100%',
  height: size.unit,
  borderRadius: size.half,
  overflow: 'hidden',
  backgroundColor: color.background.complementary(5),
}));

interface BarFillProps {
  $percentage: number;
}

export const BarFill = styled.div<BarFillProps>(
  ({ theme: { color, transition }, $percentage }) => ({
    height: '100%',
    width: `${$percentage}%`,
    borderRadius: 'inherit',
    transition: transition.standard('width'),
    backgroundColor: color.background.complementary(6),
  }),
);
